import { unitProgress, type CurriculumUnit } from '../quran/curriculumProgress';
import type { SurahCoverage } from '../quran/trackProgress';

/**
 * The term's curriculum drawn as a map of stages — م-5-01.
 *
 * "يُعرض المقرر كخريطة مراحل، تُفتح المرحلة التالية عند بلوغ حدّ الإتقان في
 * سابقتها."
 *
 * Derived from the two things that already exist: the curriculum the school
 * set, and what the student has actually recited. Nothing about the map is
 * stored, so it cannot fall out of step with the coverage it draws — and a
 * curriculum edited mid-term redraws the map instead of stranding a stored
 * copy of the old one.
 *
 * The map is a VIEW, not a gate. Nothing in the app refuses a recitation
 * because its stage is locked: a معلم takes the child through whatever they
 * bring, and a child who recites ahead of the map is credited for it (see
 * `done` below). Locking says "not yet reached", never "not allowed".
 */

/** The share of a unit that counts as mastered when nothing else is said. */
export const DEFAULT_MASTERY = 80;

export type StageState = 'done' | 'open' | 'locked';

export type MapStage = {
  /** Position in the curriculum, 1-based — what a child calls "المرحلة ٣". */
  number: number;
  unit: CurriculumUnit;
  covered: number;
  total: number;
  percent: number;
  state: StageState;
};

export type MapInput = {
  coverage: Record<string, SurahCoverage>;
  units: CurriculumUnit[];
  /** Percent of a stage that opens the next one. Out-of-range values fall back. */
  mastery?: number;
};

/** The threshold to use: a school's own, or the default when it is not sane. */
export function masteryOf(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return DEFAULT_MASTERY;
  if (value < 1 || value > 100) return DEFAULT_MASTERY;
  return Math.round(value);
}

/**
 * One stage per unit, in CURRICULUM order.
 *
 * Order is the curriculum's own, never sorted: a term that sets الفاتحة after
 * البقرة meant it, and a map that reorders the school's plan is showing the
 * child a different plan.
 */
export function curriculumMap(input: MapInput): MapStage[] {
  const { coverage, units } = input;
  const mastery = masteryOf(input.mastery);

  let previousMastered = true; // The first stage is always open.
  return units.map((unit, i) => {
    const { covered, total, percent } = unitProgress(coverage, unit);
    const mastered = total > 0 && percent >= mastery;

    /*
     * A mastered stage reads `done` wherever it sits. A child who recited
     * ahead — with a معلم outside class, or in Ramadan — has done the work,
     * and a map that showed it as locked would be telling them it does not
     * count.
     */
    const state: StageState = mastered ? 'done' : previousMastered ? 'open' : 'locked';
    previousMastered = mastered;

    return { number: i + 1, unit, covered, total, percent, state };
  });
}

/** What the map says at a glance, and what the header shows above it. */
export function mapTally(stages: MapStage[]): {
  done: number; open: number; locked: number; total: number; percent: number;
} {
  const done = stages.filter((s) => s.state === 'done').length;
  const open = stages.filter((s) => s.state === 'open').length;
  return {
    done,
    open,
    locked: stages.length - done - open,
    total: stages.length,
    // Stages, not ayahs: this is the map's own progress bar. The weighted
    // ayah figure is curriculumProgress, and the two answer different
    // questions — "how far along the map" and "how much of the term".
    percent: stages.length === 0 ? 0 : Math.round((done / stages.length) * 100),
  };
}

/** The stage a child taps first: the earliest one still open. */
export function currentStage(stages: MapStage[]): MapStage | null {
  return stages.find((s) => s.state === 'open') ?? null;
}
